import { useGenericCart } from '@contexts/GenericCartContext/GenericCartContext';
import {
    Product,
    EpiProduct,
    useGetProductCategoriesMutation,
    useGetProductsByCategoryMutation,
    useGetEpiProductCategoriesMutation,
    useGetEpiProductsByCategoryMutation
} from '@services';
import { useEffect, useMemo, useState } from 'react';

type ProductType = Product | EpiProduct;

export const useGenericProductDisplayer = (categoryId: string | undefined, type: 'appro' | 'epi') => {
    const [products, setProducts] = useState<ProductType[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<ProductType | null>(null);
    const [quantity, setQuantity] = useState<number>(1);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const { items, addItem, removeItem } = useGenericCart({ type });

    const [getProducts, { data, isLoading, isError }] = useGetProductsByCategoryMutation();
    const [getEpiProducts, { data: epiData, isLoading: isEpiLoading, isError: isEpiError }] = useGetEpiProductsByCategoryMutation();

    const [categoryTitle, setCategoryTitle] = useState<string | undefined>();
    const [getCategories, { data: categoriesData }] = useGetProductCategoriesMutation();
    const [getEpiCategories, { data: epiCategoriesData }] = useGetEpiProductCategoriesMutation();

    const toTitleCase = (phrase: string) =>
        phrase
            .toLowerCase()
            .split(' ')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    const handleBasketClick = (product: ProductType) => {
        setSelectedProduct(product);
        const existingItem = items.find((item) => item.id === product.id);
        setQuantity(existingItem ? existingItem.quantity : 1);
        setShowModal(true);
    };

    const handleValidateQuantity = () => {
        if (selectedProduct) {
            if (quantity === 0) {
                const existingItem = items.find((item) => item.id === selectedProduct.id);
                if (existingItem) {
                    removeItem(selectedProduct.id);
                }
            } else {
                addItem({
                    id: selectedProduct.id,
                    label: selectedProduct.label,
                    quantity,
                    packing_unit: selectedProduct.packing_unit,
                    image: selectedProduct.image
                });
            }
        }
        setShowModal(false);
    };

    const filteredProducts = useMemo(() => {
        if (!searchTerm.trim()) {
            return products;
        }
        return products.filter((product) => product.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [products, searchTerm]);

    useEffect(() => {
        if (categoryId) {
            if (type === 'appro') {
                getProducts(categoryId);
                getCategories();
            } else {
                getEpiProducts(categoryId);
                getEpiCategories();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId, type]);

    useEffect(() => {
        if (type === 'appro') {
            if (isLoading) {
                return;
            }

            if (data) {
                setProducts(data);
            }

            if (isError) {
                setErrorMessage('Une erreur est survenue lors du chargement des produits');
            }
        } else {
            if (isEpiLoading) {
                return;
            }

            if (epiData) {
                setProducts(epiData);
            }

            if (isEpiError) {
                setErrorMessage('Une erreur est survenue lors du chargement des produits EPI');
            }
        }
    }, [data, epiData, isLoading, isEpiLoading, isError, isEpiError, type]);

    useEffect(() => {
        if (type === 'appro') {
            if (categoriesData) {
                const categoryTitleToFormat = categoriesData.find((category) => category.id === categoryId)?.label;
                if (categoryTitleToFormat) {
                    setCategoryTitle(toTitleCase(categoryTitleToFormat));
                }
            }
        } else {
            if (epiCategoriesData) {
                const categoryTitleToFormat = epiCategoriesData.find((category) => category.id === categoryId)?.label;
                if (categoryTitleToFormat) {
                    setCategoryTitle(toTitleCase(categoryTitleToFormat));
                }
            }
        }
    }, [categoriesData, epiCategoriesData, categoryId, type]);

    return {
        products,
        isLoading: type === 'appro' ? isLoading : isEpiLoading,
        errorMessage,
        toTitleCase,
        categoryTitle,
        showModal,
        setShowModal,
        quantity,
        setQuantity,
        items,
        handleBasketClick,
        handleValidateQuantity,
        searchTerm,
        setSearchTerm,
        filteredProducts
    };
};
